import { GetQuestionReviewTaskByIdDocument, useUpdateTaskMutation } from '@exam-management/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  FormButton,
  FormDateInput,
  useTheme,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useQuestionReviewTaskDetails } from '../../providers';

const EditDueDateFormSchema = z.object({
  dueDate: z.date({ message: 'กรุณาเลือก Due Date' }),
});

type EditDueDateFormValues = z.infer<typeof EditDueDateFormSchema>;

export interface EditDueDateModalReviewer {
  id: string;
  dueDate: Date | null;
  assigneeId: string;
}

export interface EditDueDateModalProps {
  open: boolean;
  reviewer: EditDueDateModalReviewer | null;
  onClose: () => void;
}

export default function EditDueDateModal(props: Readonly<EditDueDateModalProps>) {
  const { open, reviewer, onClose } = props;

  const theme = useTheme();
  const { showPromiseSnackbar } = useSnackbar();
  const { taskDetails } = useQuestionReviewTaskDetails();

  const formContext = useForm<EditDueDateFormValues>({
    resolver: zodResolver(EditDueDateFormSchema),
  });

  const [updateTask, { loading: isUpdatingDueDate }] = useUpdateTaskMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQuestionReviewTaskByIdDocument,
        variables: {
          input: {
            taskId: taskDetails.id,
          },
        },
      },
    ],
  });

  useEffect(() => {
    if (!open || !reviewer) {
      formContext.reset();
      return;
    }

    formContext.reset(
      reviewer.dueDate
        ? {
            dueDate: reviewer.dueDate,
          }
        : undefined,
    );
  }, [formContext, open, reviewer]);

  const handleClose = () => {
    if (isUpdatingDueDate) return;

    formContext.reset();
    onClose();
  };

  const handleEditDueDate = async (data: EditDueDateFormValues) => {
    if (!reviewer?.id || !reviewer.assigneeId) return;

    await updateTask({
      variables: {
        updateTaskInput: {
          id: reviewer.id,
          name: taskDetails.name ?? '',
          assigneeId: reviewer.assigneeId,
          dueDate: data.dueDate,
        },
      },
    });

    formContext.reset({ dueDate: data.dueDate });
    onClose();
  };

  const handleSubmit = (data: EditDueDateFormValues) => {
    showPromiseSnackbar({
      message: {
        success: 'Due date has been updated',
        error: 'Failed to update due date',
        pending: 'Updating due date...',
      },
      promise: () => handleEditDueDate(data),
    });
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown={isUpdatingDueDate}
        maxWidth='sm'
        fullWidth
        slotProps={{
          paper: {
            component: Form,
            onSubmit: formContext.handleSubmit(handleSubmit),
          },
          transition: {
            onExited: () => formContext.reset(),
          },
        }}
      >
        <DialogTitle onClose={handleClose}>Edit Due Date</DialogTitle>
        <Divider />
        <DialogContent>
          <FormDateInput
            name='dueDate'
            label='Due Date'
            inputFormat='DD-MM-YYYY'
            placeholder='กรุณาเลือก Due Date'
            minDate={new Date()}
            zIndex={theme.zIndex.modal + 1}
            withinPortal
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            onClick={handleClose}
            disabled={isUpdatingDueDate}
          >
            Cancel
          </Button>
          <FormButton label='Save' type='submit' loading={isUpdatingDueDate} />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
